import { useEffect, useId, useState } from "react";
import { createPortal } from "react-dom";

import { api, ApiError, type PlayerProfile, type ReplaceOptions, type ReplaceResult } from "../lib/api";

interface ReplacePlayerDialogProps {
  token: string;
  tournamentId: number;
  participantId: number;
  /** Ya se guardó: `result.summary` es el aviso para la pantalla. */
  onDone: (result: ReplaceResult) => void;
  onClose: () => void;
}

type Source = "registered" | "catalog";

function byName<T>(items: T[], name: (item: T) => string): T[] {
  return [...items].sort((a, b) => name(a).localeCompare(name(b), "es", { sensitivity: "base" }));
}

/** Texto de la confirmación (Eduardo 2026-10-14). */
export function replaceConfirmText(outName: string, inName: string, groupName: string | null): string {
  const where = groupName ? `en el ${groupName.toUpperCase()}` : "en el cuadro";
  return `${outName} sale y ${inName} toma su lugar ${where} con los mismos partidos, fechas y canchas. ${outName} queda dado de baja.`;
}

/**
 * «Reemplazar jugador» (Eduardo 2026-10-14): alguien que no ha jugado nada se
 * va y otro toma su lugar tal cual (mismo grupo, mismo lugar, mismos
 * partidos con su fecha, hora y cancha). No rehace el grupo ni manda avisos.
 */
export default function ReplacePlayerDialog({ token, tournamentId, participantId, onDone, onClose }: ReplacePlayerDialogProps) {
  const ids = useId();
  const [options, setOptions] = useState<ReplaceOptions | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [source, setSource] = useState<Source>("registered");
  const [registeredId, setRegisteredId] = useState<number | "">("");
  const [search, setSearch] = useState("");
  const [catalog, setCatalog] = useState<PlayerProfile[]>([]);
  const [catalogId, setCatalogId] = useState<number | "">("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let alive = true;
    api.groups
      .replaceOptions(token, tournamentId, participantId)
      .then((response) => {
        if (!alive) return;
        setOptions(response.data);
        if (response.data.candidates.length === 0) setSource("catalog");
      })
      .catch((err) => {
        if (alive) setError(err instanceof ApiError ? err.message || "No se pudo cargar." : "Error de red. Intenta otra vez.");
      });
    return () => {
      alive = false;
    };
  }, [token, tournamentId, participantId]);

  useEffect(() => {
    if (source !== "catalog" || !options?.allows_new_player) return;
    let alive = true;
    const timer = window.setTimeout(() => {
      api.players
        .search(token, { search: search.trim() || undefined, limit: 50 })
        .then((response) => {
          if (alive) setCatalog(byName(response.data.players, (p) => p.display_name || ""));
        })
        .catch(() => {
          if (alive) setCatalog([]);
        });
    }, 250);
    return () => {
      alive = false;
      window.clearTimeout(timer);
    };
  }, [source, search, token, options?.allows_new_player]);

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape" && !saving) onClose();
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [onClose, saving]);

  const candidates = options ? byName(options.candidates, (c) => c.name) : [];
  const incomingName =
    source === "registered"
      ? candidates.find((c) => c.participant_id === registeredId)?.name ?? null
      : catalog.find((p) => p.id === catalogId)?.display_name ?? null;
  const ready = Boolean(options?.can_replace && incomingName && !saving);

  async function handleApply() {
    if (!options || !ready) return;
    setSaving(true);
    setError(null);
    try {
      const response = await api.groups.replaceParticipant(
        token,
        tournamentId,
        participantId,
        source === "registered"
          ? { replacement_participant_id: Number(registeredId) }
          : { player_profile_id: Number(catalogId) },
      );
      onDone(response.data);
    } catch (err) {
      setSaving(false);
      setError(err instanceof ApiError ? err.message || "No se pudo reemplazar." : "Error de red. Intenta otra vez.");
    }
  }

  return createPortal(
    <div className="rr-change-sheet" role="dialog" aria-modal="true" aria-labelledby={`${ids}-title`}>
      <div className="rr-change-sheet__backdrop" onClick={() => !saving && onClose()} />
      <div className="rr-change-sheet__panel">
        <header className="rr-change-sheet__header">
          <div>
            <span className="player-pro-eyebrow">Reemplazar jugador</span>
            <strong id={`${ids}-title`}>
              {options ? options.outgoing.name : "Cargando…"}
              {options?.group ? ` · ${options.group.name}` : ""}
            </strong>
          </div>
          <button type="button" className="rr-change-sheet__close" onClick={onClose} disabled={saving} aria-label="Cerrar">
            ×
          </button>
        </header>

        <div className="rr-change-sheet__body" data-no-toast>
          {options && !options.can_replace ? (
            <p className="field-warning">{options.reason}</p>
          ) : null}
          {options && options.can_replace ? (
            <>
              <p className="muted">
                Quien entra juega sus {options.matches} {options.matches === 1 ? "partido" : "partidos"} con la misma fecha,
                hora y cancha. El grupo no se rehace y no se manda ningún aviso.
              </p>
              <fieldset className="rr-change-sheet__modes" disabled={saving}>
                <legend className="sr-only">¿Quién entra?</legend>
                <label className={`rr-change-sheet__mode${source === "registered" ? " is-active" : ""}`}>
                  <input
                    type="radio"
                    name={`${ids}-source`}
                    checked={source === "registered"}
                    onChange={() => setSource("registered")}
                  />
                  <span>Un inscrito sin grupo</span>
                </label>
                {source === "registered" ? (
                  <label className="rr-change-sheet__field rr-change-sheet__field--nested">
                    <span>Jugador inscrito</span>
                    {candidates.length === 0 ? (
                      <small className="muted">No hay inscritos sin grupo en esta categoría.</small>
                    ) : (
                      <select
                        value={registeredId}
                        onChange={(e) => setRegisteredId(e.target.value ? Number(e.target.value) : "")}
                      >
                        <option value="">Elige…</option>
                        {candidates.map((c) => (
                          <option key={c.participant_id} value={c.participant_id}>
                            {c.name}
                          </option>
                        ))}
                      </select>
                    )}
                  </label>
                ) : null}
                {options.allows_new_player ? (
                  <label className={`rr-change-sheet__mode${source === "catalog" ? " is-active" : ""}`}>
                    <input
                      type="radio"
                      name={`${ids}-source`}
                      checked={source === "catalog"}
                      onChange={() => setSource("catalog")}
                    />
                    <span>Otro jugador del catálogo (se inscribe en esta categoría)</span>
                  </label>
                ) : null}
                {source === "catalog" && options.allows_new_player ? (
                  <div className="rr-change-sheet__field rr-change-sheet__field--nested">
                    <label>
                      <span>Buscar jugador</span>
                      <input
                        type="search"
                        value={search}
                        placeholder="Nombre"
                        onChange={(e) => {
                          setSearch(e.target.value);
                          setCatalogId("");
                        }}
                      />
                    </label>
                    <label>
                      <span>Jugador</span>
                      <select value={catalogId} onChange={(e) => setCatalogId(e.target.value ? Number(e.target.value) : "")}>
                        <option value="">Elige…</option>
                        {catalog.map((p) => (
                          <option key={p.id} value={p.id}>
                            {p.display_name}
                          </option>
                        ))}
                      </select>
                    </label>
                  </div>
                ) : null}
              </fieldset>
              {incomingName ? (
                <p className="rr-change-sheet__summary replace-player__confirm">
                  {replaceConfirmText(options.outgoing.name, incomingName, options.group?.name ?? null)}
                </p>
              ) : null}
            </>
          ) : null}
          {error ? (
            <p role="alert" className="form-error">
              {error}
            </p>
          ) : null}
        </div>

        <footer className="rr-change-sheet__footer">
          <button type="button" className="btn btn--ghost" onClick={onClose} disabled={saving}>
            No, regresar
          </button>
          <button type="button" className="btn" onClick={() => void handleApply()} disabled={!ready}>
            {saving ? "Guardando…" : "Reemplazar jugador"}
          </button>
        </footer>
      </div>
    </div>,
    document.body,
  );
}
